import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useBillingOverviewQuery,
  useCreateInvoiceMutation,
  type BillingOverview,
  type PaidTier,
} from '@entities/Plan';
import { useProductEventOnMount } from '@shared/analytics';
import { LANDING_URL, paths } from '@shared/config';
import { cn } from '@shared/lib';
import { CheckIcon, TokenIcon } from '@shared/ui';
import { useIsSidebarMobile } from '@widgets/Sidebar';
import { BillingDetails, DonateCard } from './BillingDetails';
import { showInvoiceError } from './invoiceError';
import {
  usePlansView,
  type CheckoutView,
  type FeatureView,
  type OptionView,
  type StatusView,
} from './usePlansView';
import './PlansPage.css';

function ArrowIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 10h12M11 5l5 5-5 5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
    </svg>
  );
}

interface HeaderProps {
  title?: string;
  titleEm?: string;
  /** Только на мобайле: страница открыта как полноэкранный поток с ✕. */
  onClose?: () => void;
}

function PlansHeader({ title, titleEm, onClose }: HeaderProps) {
  const { t } = useTranslation();
  return (
    <header className="pl-head">
      <div className="pl-head-row">
        <p className="pl-eyebrow">{t('billing.title')}</p>
        {onClose && (
          <button type="button" className="pl-close" onClick={onClose} aria-label={t('billing.shop.close')}>
            <CloseIcon />
          </button>
        )}
      </div>
      {title && <h1 className="pl-title">{title} <em>{titleEm}</em></h1>}
    </header>
  );
}

function AccessStatus({ status, compact }: { status: StatusView; compact: boolean }) {
  const { t } = useTranslation();
  // Баланс: остаток на сегодня, полоса пустеет с каждой тратой (2026-10-13).
  const exhausted = status.limit > 0 && status.left <= 0;
  const leftPct = status.limit > 0 ? Math.min(100, (status.left / status.limit) * 100) : 0;
  const low = !exhausted && leftPct <= 20;
  return (
    <section
      className="pl-status"
      data-tone={status.tone}
      aria-label={t('billing.shop.status.label')}
    >
      <div className="pl-days">
        <div className="pl-badge-row">
          <span className="pl-badge">{status.badge}</span>
          <span className="pl-badge-note">{status.badgeNote}</span>
        </div>
        <p className="pl-status-title">{status.title}</p>
        {status.daysPct !== null && (
          <div className="pl-bar pl-bar--tone" aria-hidden="true"><span style={{ width: `${status.daysPct}%` }} /></div>
        )}
        <p className="pl-note">{status.daysNote}</p>
      </div>
      <div className={cn('pl-quota', exhausted && 'pl-quota--out', low && 'pl-quota--low', status.limit === 0 && 'pl-quota--off')}>
        <div className="pl-quota-head">
          <span className="pl-label">{t(compact ? 'billing.shop.status.quotaShort' : 'billing.shop.status.quota')}</span>
          <span className="pl-quota-count">
            <TokenIcon size={compact ? 22 : 36} className="pl-quota-token" />
            <span className="pl-quota-used">{status.left}</span>
            <span className="pl-quota-limit">/ {status.limit}</span>
          </span>
        </div>
        <div className="pl-bar" aria-hidden="true"><span style={{ width: `${leftPct}%` }} /></div>
        <p className="pl-note">{status.usageNote}</p>
        {status.bonus > 0 && <p className="pl-note pl-quota-bonus">{t('billing.shop.status.bonus', { count: status.bonus })}</p>}
      </div>
    </section>
  );
}

function FeatureList({ features }: { features: FeatureView[] }) {
  const { t } = useTranslation();
  return (
    <ol className="pl-features">
      {features.map((feature, index) => (
        <li key={feature.id} className={cn('pl-feature', !feature.on && 'pl-feature--off')}>
          <span className="pl-feature-n" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
          <div className="pl-feature-body">
            <p className="pl-feature-title">{t(`billing.shop.features.${feature.id}`)}</p>
            <p className="pl-feature-text">{t(`billing.shop.features.${feature.id}Text`)}</p>
          </div>
          <span className="pl-feature-tag">{feature.tag}</span>
        </li>
      ))}
    </ol>
  );
}

interface PlanOptionProps {
  option: OptionView;
  disabled: boolean;
  onSelect: (tier: PaidTier) => void;
}

function PlanOption({ option, disabled, onSelect }: PlanOptionProps) {
  const className = cn('pl-option', `pl-option--${option.state}`);
  const body = (
    <>
      <span className="pl-ring" aria-hidden="true">
        {option.state === 'current' ? <CheckIcon /> : <span className="pl-ring-dot" />}
      </span>
      <span className="pl-option-body">
        <span className="pl-option-head">
          <span className="pl-option-name">{option.name}</span>
          <span className="pl-option-price">{option.price}</span>
        </span>
        <span className="pl-option-sub">{option.sub}</span>
        {option.tag && <span className="pl-option-tag">{option.tag}</span>}
      </span>
    </>
  );
  if (!option.selectable) return <div className={className}>{body}</div>;
  return (
    <button
      type="button"
      className={className}
      aria-pressed={option.state === 'selected'}
      disabled={disabled}
      onClick={() => onSelect(option.tier)}
    >
      {body}
    </button>
  );
}

interface PayButtonProps {
  label: string;
  payable: boolean;
  busy: boolean;
  paying: boolean;
  withArrow?: boolean;
  onPay: () => void;
}

function PayButton({ label, payable, busy, paying, withArrow = false, onPay }: PayButtonProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className={cn('pl-cta', !payable && 'pl-cta--idle')}
      disabled={!payable || busy}
      aria-busy={paying}
      onClick={onPay}
    >
      {paying ? t('plans.payPending') : label}
      {withArrow && payable && !paying && <ArrowIcon />}
    </button>
  );
}

interface CheckoutProps {
  checkout: CheckoutView;
  /** Мобайл: без карточки, итог и кнопка — в нижней панели. */
  compact: boolean;
  busy: boolean;
  paying: boolean;
  onSelect: (tier: PaidTier) => void;
  onPay: () => void;
}

function Checkout({ checkout, compact, busy, paying, onSelect, onPay }: CheckoutProps) {
  const titleId = useId();
  return (
    <section className={cn('pl-checkout', !compact && 'pl-card pl-panel')} aria-labelledby={titleId}>
      <h2 id={titleId} className="pl-label">{checkout.title}</h2>
      <div className="pl-options">
        {checkout.options.map((option) => (
          <PlanOption key={option.tier} option={option} disabled={busy} onSelect={onSelect} />
        ))}
      </div>
      {!compact && <div className="pl-divider" />}
      <dl className="pl-rows">
        {checkout.rows.map((row) => (
          <div key={row.label}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
      {!compact && (
        <>
          {checkout.total && (
            <div className="pl-total">
              <span>{checkout.total.label}</span>
              <strong>{checkout.total.value}</strong>
            </div>
          )}
          <PayButton label={checkout.cta} payable={checkout.payTier != null} busy={busy} paying={paying} withArrow onPay={onPay} />
          <p className="pl-foot">{checkout.foot}</p>
        </>
      )}
    </section>
  );
}

function CheckoutBar({ checkout, busy, paying, onPay }: Omit<CheckoutProps, 'compact' | 'onSelect'>) {
  return (
    <div className="pl-paybar">
      <div className="pl-paybar-inner">
        <div className="pl-paybar-row">
          {checkout.total && (
            <div className="pl-paybar-total">
              <span>{checkout.total.label}</span>
              <strong>{checkout.total.value}</strong>
            </div>
          )}
          <PayButton label={checkout.ctaShort} payable={checkout.payTier != null} busy={busy} paying={paying} onPay={onPay} />
        </div>
        <p className="pl-foot">{checkout.footShort}</p>
      </div>
    </div>
  );
}

function PlansContent({ overview, mobile, onClose }: { overview: BillingOverview; mobile: boolean; onClose?: () => void }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<PaidTier>('PRO');
  const [donationPending, setDonationPending] = useState(false);
  const invoice = useCreateInvoiceMutation();
  const invoiceLock = useRef(false);
  const view = usePlansView(overview, selected);
  const busy = invoice.isPending || donationPending;

  const pay = () => {
    const tier = view.checkout.payTier;
    if (!tier || busy || invoiceLock.current) return;
    invoiceLock.current = true;
    invoice.mutate({ tier, slot: 'current' }, {
      onError: (error) => showInvoiceError(error, t),
      onSettled: () => { invoiceLock.current = false; },
    });
  };

  const header = <PlansHeader title={view.title} titleEm={view.titleEm} onClose={onClose} />;
  const status = <AccessStatus status={view.status} compact={mobile} />;
  const features = <FeatureList features={view.features} />;
  const checkout = (
    <Checkout
      checkout={view.checkout}
      compact={mobile}
      busy={busy}
      paying={invoice.isPending}
      onSelect={setSelected}
      onPay={pay}
    />
  );
  const donate = (
    <DonateCard
      presets={overview.donatePresets}
      min={overview.donateMin}
      compact={mobile}
      invoicePending={invoice.isPending}
      onPendingChange={setDonationPending}
    />
  );

  return (
    <>
      <div className="pl-top">
        {mobile ? (
          <>{header}{status}{checkout}{features}{donate}</>
        ) : (
          <>
            <div className="pl-main">{header}{status}{features}</div>
            <div className="pl-aside">{checkout}{donate}</div>
          </>
        )}
      </div>
      <BillingDetails overview={overview} compact={mobile} />
      <p className="pl-legal">
        {t('billing.shop.legalPrefix')}{' '}
        <a href={`${LANDING_URL}/offer`} target="_blank" rel="noopener noreferrer">{t('billing.shop.legalLink')}</a>.
      </p>
      {mobile && <CheckoutBar checkout={view.checkout} busy={busy} paying={invoice.isPending} onPay={pay} />}
    </>
  );
}

/**
 * Тарифы по макетам Claude Design: desktop — две колонки и вкладки, mobile
 * (≤900px, как у сайдбара) — одна колонка, аккордеон и липкая панель оплаты.
 * Данные и CTA обеих раскладок строит usePlansView.
 */
export default function PlansPage() {
  const { t } = useTranslation();
  const mobile = useIsSidebarMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const overviewQuery = useBillingOverviewQuery();
  const overview = overviewQuery.data;
  // Дошёл до тарифов — в журнал событий для воронки админки (2026-10-21).
  useProductEventOnMount('plans_opened');

  // ✕ возвращает туда, откуда пришли; при прямом заходе — на главную.
  const close = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate(paths.dashboard);
  };
  const onClose = mobile ? close : undefined;

  return (
    <div className={cn('pl', mobile && 'pl--mobile')}>
      {overview ? (
        <PlansContent overview={overview} mobile={mobile} onClose={onClose} />
      ) : overviewQuery.isLoading ? (
        <>
          <PlansHeader onClose={onClose} />
          <div className="pl-top" role="status" aria-label={t('billing.shop.loading')}>
            <div className="pl-main">
              <div className="pl-skeleton pl-skeleton--title" />
              <div className="pl-skeleton pl-skeleton--status" />
            </div>
            <div className="pl-aside">
              <div className="pl-skeleton pl-skeleton--panel" />
            </div>
          </div>
        </>
      ) : (
        <>
          <PlansHeader onClose={onClose} />
          <div className="pl-error" role="alert">
            <p>{t('billing.loadError')}</p>
            <button type="button" className="pl-retry" disabled={overviewQuery.isFetching} onClick={() => overviewQuery.refetch()}>
              {t('billing.shop.retry')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
